import { DownOutlined, MailOutlined, UserOutlined } from '@ant-design/icons'
import ProForm, { ProFormCaptcha } from '@ant-design/pro-form'
import { Button, Col, ConfigProvider, Form, Input, Modal, Row, Select, message } from 'antd'
import enUS from 'antd/lib/locale/en_US'
import thTH from 'antd/lib/locale/th_TH'
import zhHK from 'antd/lib/locale/zh_HK'
import OrderAppLib from 'dimorder-orderapp-lib'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import Card from '@/components/Card'
import LanguageOption from '@/components/LanguageOption'
import Loading from '@/components/Loading'
import customerApi from '@/libs/api/customerApi'
import logger, { logId } from '@/libs/logger'
import { getAvailablePhoneCountries } from '@/libs/phoneCountries'
import { isRegion, REGIONS } from '@/libs/region'
import { actions, useDispatch, useSelector } from '@/redux'
import colors from '@/style/colors'

const isHKRegion = isRegion(process.env.REACT_APP_REGION, REGIONS.HK)

export default function Auth(props) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [registerUserName, setRegisterUserName] = useState(false)
  const { isLogin } = useSelector((state) => state.user)
  const { isInit } = useSelector((state) => state.app)
  const [username, setUserName] = useState()
  const [initialized, setInitialized] = useState(false)
  const [usernameLoading, setUsernameLoading] = useState(false)
  const locale = useSelector((state) => state.app.lang)
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const from = query.get('from')
  const code = query.get('code')
  const countryCodeA2 = useSelector((state) => state.app?.ipInfo?.country || state.app?.lang)

  const localeHelper = OrderAppLib.libs.createLocaleHelper({
    getCountryFromStore() {
      return countryCodeA2
    },
  })

  const dispatch = useDispatch()

  const reset = () => {
    setRegisterUserName(false)
    setUserName(undefined)
    setUsernameLoading(false)
  }

  const loginMerchant = () => {
    return dispatch(
      actions.merchant.openMerchantLoginDialog({
        data: { title: t('app.component.merchant.addMerchant') },
        firstLogin: true,
        onSuccess: reset,
      }),
    )
  }

  const onLogin = useCallback(
    async (value, isKConnect = false) => {
      try {
        let responseData
        if (isKConnect) {
          responseData = await customerApi.kconnectLogin(value)
        } else {
          value.countryCodeA2 = value.countryCodeA2 || localeHelper.getPreferredCountry()?.a2
          value.phone = value.phone || ''
          const fullPhone = OrderAppLib.libs.getParsedPhoneNumber(value.countryCodeA2, value.phone)
          responseData = await customerApi.login(fullPhone, value.otp)
        }
        dispatch(actions.user.updateCustomerApiAuth(responseData.data))
        const accountData = await dispatch(actions.user.getCustomerInfo())
        await dispatch(actions.merchant.init(accountData))
        if (checkEmptyMerchants(accountData)) {
          setRegisterUserName(true)
        } else {
          dispatch(actions.user.login())
          dispatch(actions.merchant.closeMerchantLoginDialog())
          reset()
        }
      } catch (error) {
        logger.log('[login] Login Error', { error })
        Modal.error({
          title: `${t('page.auth.error.otp.input.title')} (${logId})`,
          content: t('page.auth.error.otp.input.msg'),
        })
      }
    },
    [dispatch, t, localeHelper],
  )

  useEffect(() => {
    if (from === 'kconnect' && !initialized) {
      setInitialized(true)
      onLogin(code, true)
    }
  }, [initialized, from, onLogin, code])

  useEffect(() => {
    if (!isInit) {
      dispatch(actions.app.init())
    }
  }, [dispatch, isInit])

  // 監聽語言變化，更新表單錯誤訊息為新語言
  useEffect(() => {
    if (form) {
      const fieldsWithErrors = form.getFieldsError().filter(({ errors }) => errors.length > 0)
      if (fieldsWithErrors.length > 0) {
        const fieldNames = fieldsWithErrors.map(({ name }) => name)
        form.validateFields(fieldNames).catch(() => {})
      }
    }
  }, [locale, form])

  const onUserNameChange = (name) => setUserName(name.target.value)

  const checkEmptyMerchants = (accountData) => {
    let result = true
    accountData.data.platforms.forEach((account) => {
      if (account.name === 'dimorder-merchant') result = false
    })
    return result
  }

  function validateUserName(name) {
    const MAX_NAME_BYTE = 20
    const regexpLA = /[a-zA-z ]/
    const regexpCJK = /[\u4E00-\u9FFF\u3400-\u4DBF]/

    if (!name) {
      message.error(t('app.component.userInfoModal.error.name.empty'))
      return false
    }

    if (name.length > MAX_NAME_BYTE) {
      message.error(
        t('app.component.userInfoModal.error.name.length', {
          cjk: MAX_NAME_BYTE / 2,
          la: MAX_NAME_BYTE,
        }),
      )
      return false
    }

    let byte = 0
    const invalidChars = []
    for (const char of name) {
      if (regexpLA.test(char)) byte += 1
      else if (regexpCJK.test(char)) byte += 2
      else invalidChars.push(char)
    }

    if (invalidChars.length > 0) {
      message.error(t('app.component.userInfoModal.error.name.regex'))
      return false
    }

    if (byte > MAX_NAME_BYTE) {
      message.error(
        t('app.component.userInfoModal.error.name.length', {
          cjk: MAX_NAME_BYTE / 2,
          la: MAX_NAME_BYTE,
        }),
      )
      return false
    }

    return true
  }

  const handleUsernameSubmit = async () => {
    if (!validateUserName(username)) return
    setUsernameLoading(true)
    await dispatch(actions.user.updateCustomerInfo({ name: username }))
    setUsernameLoading(false)
    loginMerchant()
  }

  const handleGetCaptcha = useCallback(
    async (phoneValue) => {
      try {
        const a2 = form.getFieldValue('countryCodeA2')
        const fullPhone = OrderAppLib.libs.getParsedPhoneNumber(a2, phoneValue)
        await customerApi.sendOtp(fullPhone)
        await message.success(`${t('page.auth.message.otp.success')}${phoneValue}!`)
      } catch (error) {
        Modal.error({
          title: t('page.auth.error.otp.send.title'),
          content: t('page.auth.error.otp.send.msg'),
        })
        throw error
      }
    },
    [form, t],
  )

  // === 動態量測 Select 寬度（做法B） ===
  const [ccWidth, setCcWidth] = useState(100)

  const measureCcWidth = useCallback((label) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // 與 antd 預設字型接近；若你有全域樣式覆寫，請同步調整
    ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial'
    const m = ctx.measureText(label)
    // padding：左右文字留白 + 下拉箭頭空間（可依實際觀感微調）
    const padding = 40
    const minW = 84
    const maxW = 220
    const width = Math.min(
      maxW,
      Math.max(minW, Number((Math.ceil(m.width + padding) + 3.5).toFixed(1))),
    ) // 3.5 是因為測量結果偏小一點點
    setCcWidth(width)
  }, [])

  // 初始化 & 語言或預設國變更後重算
  useEffect(() => {
    const a2 =
      form.getFieldValue('countryCodeA2') || localeHelper.getPreferredCountry()?.a2 || countryCodeA2
    const c = localeHelper.getPhoneCountries().find((x) => x.a2 === a2)
    const label = `${c?.label ?? ''} ${c?.code?.replace('+', '') ?? ''}`.trim()
    if (label) measureCcWidth(label)
  }, [locale, countryCodeA2, form, localeHelper, measureCcWidth])

  if (!isInit) return <Loading />

  const phoneCountries = getAvailablePhoneCountries(
    localeHelper.getPhoneCountries(),
    process.env.REACT_APP_REGION,
  )

  return (
    <ConfigProvider locale={locale === 'zh-HK' ? zhHK : locale === 'th-TH' ? thTH : enUS}>
      {isLogin ? (
        props.children
      ) : (
        <Row style={{ display: 'flex', justifyContent: 'space-around', margin: '1em' }}>
          <Col xl={8} xs={24}>
            <Card
              color={colors.white}
              width='100%'
              height='100%'
              textAlign='center'
              padding='4em 2em 12em'
            >
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  style={{ height: '70px' }}
                  alt='logo'
                  src='https://img.dimorder.com/414x/public/Landing_Banner/logo/dimpos-v2.png'
                />
              </span>
              <h1
                style={{ textAlign: 'center', marginTop: '1em', marginBottom: '1em', fontSize: 35 }}
              >
                {t('page.auth.title')}
              </h1>

              {!registerUserName && (
                <ProForm
                  form={form}
                  initialValues={{
                    countryCodeA2: isHKRegion
                      ? REGIONS.HK
                      : localeHelper.getPreferredCountry()?.a2 || countryCodeA2,
                    phone: '',
                  }}
                  onFinish={onLogin}
                  submitter={{
                    searchConfig: { submitText: t('page.auth.login') },
                    render: (_, dom) => dom.pop(),
                    submitButtonProps: { size: 'large', style: { width: '100%' } },
                  }}
                >
                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.phone !== curr.phone || prev.countryCodeA2 !== curr.countryCodeA2
                    }
                  >
                    {({ getFieldValue }) => {
                      const currentA2 =
                        getFieldValue('countryCodeA2') ||
                        localeHelper.getPreferredCountry()?.a2 ||
                        countryCodeA2
                      const selectedCountry = localeHelper
                        .getPhoneCountries()
                        .find((c) => c.a2 === currentA2)
                      const a2 = selectedCountry?.a2

                      return (
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'stretch',
                            width: '100%',
                          }}
                        >
                          {/* 國碼 Select（動態寬度） */}
                          <Form.Item
                            name='countryCodeA2'
                            style={{ marginBottom: 0 }}
                            rules={[{ required: true, message: t('page.auth.error.phone.empty') }]}
                          >
                            <Select
                              size='large'
                              dropdownMatchSelectWidth={false}
                              style={{
                                width: ccWidth,
                                height: 40,
                                pointerEvents: phoneCountries.length <= 1 ? 'none' : 'auto',
                              }}
                              showArrow={phoneCountries.length > 1}
                              onChange={(nextA2) => {
                                const c = localeHelper
                                  .getPhoneCountries()
                                  .find((x) => x.a2 === nextA2)
                                const label = `${c?.label ?? ''} ${
                                  c?.code?.replace('+', '') ?? ''
                                }`.trim()
                                if (label) measureCcWidth(label)
                              }}
                            >
                              {phoneCountries.map((country) => (
                                <Select.Option key={country.code} value={country.a2}>
                                  {country.label} {country.code.replace('+', '')}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          {/* 電話 Input（吃滿剩餘空間） */}
                          <Form.Item
                            name='phone'
                            style={{ flex: 1, marginBottom: 0, minWidth: 0 }}
                            rules={[
                              { required: true, message: t('page.auth.error.phone.empty') },
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve()
                                  if (!a2) return Promise.resolve()
                                  return OrderAppLib.libs.phoneIsValid(a2, value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error(t('page.auth.error.phone.format')))
                                },
                              },
                            ]}
                          >
                            <Input
                              size='large'
                              maxLength={selectedCountry?.maxLength ?? 10}
                              placeholder={t('page.auth.input.phone')}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </div>
                      )
                    }}
                  </Form.Item>

                  <ProFormCaptcha
                    fieldProps={{ size: 'large', prefix: <MailOutlined /> }}
                    captchaProps={{ size: 'large' }}
                    captchaTextRender={(timing, count) =>
                      timing ? `${count} ${t('page.auth.otp.send')}` : t('page.auth.otp.send')
                    }
                    phoneName='phone'
                    name='otp'
                    rules={[{ required: true, message: t('page.auth.error.otp.empty') }]}
                    placeholder={t('page.auth.input.otp')}
                    onGetCaptcha={handleGetCaptcha}
                  />
                </ProForm>
              )}

              {registerUserName && (
                <>
                  <Input
                    prefix={<UserOutlined className='site-form-item-icon' />}
                    value={username}
                    onChange={onUserNameChange}
                    size='large'
                    style={{ width: '100%', marginTop: 20 }}
                    allowClear
                    placeholder={t('page.auth.input.username')}
                  />
                  <Button
                    style={{
                      width: '100%',
                      marginTop: 30,
                      marginBottom: 20,
                      background: colors.textPrimary,
                      borderColor: colors.textPrimary,
                      color: colors.white,
                    }}
                    size='large'
                    onClick={handleUsernameSubmit}
                    loading={usernameLoading}
                  >
                    {t('app.common.submit')}
                  </Button>
                </>
              )}
              <br />
              <LanguageOption position='auth' />
            </Card>
          </Col>
        </Row>
      )}
    </ConfigProvider>
  )
}
